import {
  FlexibleConnectedPositionStrategy,
  OverlayRef,
} from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import {
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import {
  getOriginPosition,
  getOverlayPosition,
  Placement,
} from '@epicuro-next/utilities';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { TooltipComponent } from '../component/tooltip.component';
import { TooltipService } from '../service/tooltip.service';

/** Tooltip directive */
@Directive({
  selector: '[atxTooltip]',
  exportAs: 'atxTooltip',
  standalone: true,
  providers: [TooltipService],
})
export class TooltipDirective implements OnDestroy, OnChanges {
  /** Tooltip text */
  @Input()
  public atxTooltip: string | TemplateRef<unknown>;

  /** Tooltip placement */
  @Input()
  public tooltipPlacement: Placement = Placement.TOP;

  /** Tooltip styling */
  @Input()
  public tooltipStyling: string;

  @Input()
  public tooltipEnabled = true;

  /** TooltipComponent instance */
  private tooltipInstance: null | TooltipComponent;
  /** OverlayRef instance */
  private overlayRef: null | OverlayRef;
  /** ComponentPortal instance */
  private portal: ComponentPortal<TooltipComponent>;
  /** Destroy Subject to be completed on directive destroy */
  private destroy$: Subject<void> = new Subject();

  /** Dependencies. */
  constructor(
    private elementRef: ElementRef,
    private viewContainerRef: ViewContainerRef,
    private tooltipService: TooltipService,
  ) {}

  /** Events which open the tooltip */
  @HostListener('focus')
  @HostListener('mouseenter')
  public onMouseEnter() {
    if (this.atxTooltip && this.tooltipEnabled) {
      this.open();
    }
  }
  /** Events which close the tooltip */
  @HostListener('focusout')
  @HostListener('mouseleave')
  @HostListener('click')
  public onMouseLeave() {
    if (this.atxTooltip && this.tooltipEnabled) {
      this.detach();
    }
  }

  /**
   * Component OnChanges callback
   */
  public ngOnChanges({ tooltipEnabled }: SimpleChanges): void {
    if (tooltipEnabled && !tooltipEnabled.currentValue) {
      this.detach();
    }
  }

  /**
   * Component OnDestroy callback
   */
  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.dispose();
  }

  /**
   * Function to create overlay and dynamically render TooltipComponent inside ComponentPortal
   */
  private open() {
    this.detach();
    this.dispose();

    const overlayRef: OverlayRef = this.getOrCreateOverlayRef();
    this.updatePosition();
    this.portal =
      this.portal ||
      new ComponentPortal(TooltipComponent, this.viewContainerRef);

    this.tooltipInstance = overlayRef.attach(this.portal).instance;
    this.tooltipInstance.content = this.atxTooltip;
    this.tooltipInstance.customClass = this.tooltipStyling;
    this.tooltipInstance.changePosition(this.tooltipPlacement);
  }

  /**
   * Set listener for changes in the tooltip position
   */
  private trackPositionChanges(position: FlexibleConnectedPositionStrategy) {
    position.positionChanges
      .pipe(takeUntil(this.getOrCreateOverlayRef().detachments()))
      .subscribe((pos) => {
        const { originX, originY } = pos.connectionPair;
        let tooltipPosition = 'right';
        if (originX === 'start' && originY === 'center') {
          tooltipPosition = 'left';
        } else if (originX === 'center' && originY === 'top') {
          tooltipPosition = 'top';
        } else if (originX === 'center' && originY === 'bottom') {
          tooltipPosition = 'bottom';
        }
        if (this.tooltipInstance) {
          this.tooltipInstance.changePosition(tooltipPosition);
        }
      });
  }

  /**
   * Create OverlayRef if not created yet
   */
  private getOrCreateOverlayRef(): OverlayRef {
    if (this.overlayRef) {
      return this.overlayRef;
    }

    this.overlayRef = this.tooltipService.createOverlay(this.elementRef);

    this.overlayRef
      .detachments()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.detach());

    return this.overlayRef;
  }

  /**
   * Updates the position of the current tooltip.
   */
  private updatePosition() {
    const position = this.getOrCreateOverlayRef().getConfig()
      .positionStrategy as FlexibleConnectedPositionStrategy;
    const origin = getOriginPosition(this.tooltipPlacement);
    const overlay = getOverlayPosition(this.tooltipPlacement);

    position.withPositions([
      { ...origin.main, ...overlay.main },
      { ...origin.fallback, ...overlay.fallback },
    ]);
    this.trackPositionChanges(position);
  }

  /**
   * Detach OverlayRef from portal
   */
  private detach() {
    if (this.overlayRef && this.overlayRef.hasAttached()) {
      this.overlayRef.detach();
    }
  }

  private dispose() {
    if (!!this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
    this.tooltipInstance = null;
  }
}
